import type { Environments, Paddle, PaddleSetupPwCustomer } from '@paddle/paddle-js';
import { billingApi } from '@/api/endpoints/billing';
import type { CheckoutParams, PaddleConfig, PlanSlug } from '@/api/types';
import { track } from '@/analytics/tracker';
import { appConfig } from '@/config/app';

/**
 * Шлюз чекаута (BILL-4): Paddle.js инициализируется один раз на сессию, все
 * события идут через одну шину. В mock-режиме Paddle не грузится — оверлей
 * рисует MockCheckout, а «оплата» завершает подписку через mock-API.
 * Покупателя для Paddle Retain называет `setPaddleCustomer` (см. `usePaddleRetain`).
 */
export interface CheckoutEvent {
  name: string;
  data?: unknown;
}

export interface OpenCheckoutOptions {
  params: CheckoutParams;
  plan: PlanSlug;
}

export interface CheckoutGateway {
  /** Готовит SDK; бросает, если конфиг или Paddle недоступны. */
  ensureReady(): Promise<void>;
  open(options: OpenCheckoutOptions): void;
}

const bus = new EventTarget();

export function onCheckoutEvent(handler: (event: CheckoutEvent) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<CheckoutEvent>).detail);
  bus.addEventListener('checkout', listener);
  return () => bus.removeEventListener('checkout', listener);
}

export function emitCheckoutEvent(event: CheckoutEvent): void {
  bus.dispatchEvent(new CustomEvent<CheckoutEvent>('checkout', { detail: event }));
}

function uiLocale(): 'ru' | 'en' {
  return document.documentElement.lang?.startsWith('ru') ? 'ru' : 'en';
}

function uiTheme(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

// ——— Live: @paddle/paddle-js ———
let paddle: Paddle | null = null;
let config: PaddleConfig | null = null;
let initPromise: Promise<void> | null = null;

// ——— Paddle Retain ———
/** Email залогиненного покупателя; null — гость, он остаётся анонимом (pwCustomer: {}). */
let retainEmail: string | null = null;

function pwCustomer(): PaddleSetupPwCustomer {
  return retainEmail ? { email: retainEmail } : {};
}

/**
 * Paddle Retain: залогиненного покупателя надо назвать Paddle.js, иначе in-app
 * подсказка «обнови способ оплаты» при past_due не покажется. Email — тот же,
 * что уходит в чекаут. До инициализации значение запоминается и уходит в
 * `initializePaddle`, после — `Update`. Retain — необязательный слой: сбой
 * Update не должен ломать оплату. В mock-режиме Paddle.js нет — no-op.
 */
export function setPaddleCustomer(email: string | null): void {
  retainEmail = email;
  if (!paddle) return;
  try {
    paddle.Update({ pwCustomer: pwCustomer() });
  } catch {
    // Retain — необязательный слой.
  }
}

async function initLive(): Promise<void> {
  const startedAt = Date.now();
  track('paddle_sdk_load_start');
  try {
    track('paddle_config_request');
    let cfg: PaddleConfig;
    try {
      cfg = await billingApi.paddleConfig();
    } catch (error) {
      track('paddle_config_failed', {
        metadata: {
          error_message: (error as Error)?.message,
          status: (error as { status?: number })?.status,
        },
      });
      throw error;
    }
    config = cfg;
    track('paddle_config_success', {
      metadata: { env: cfg.env, has_client_token: !!cfg.client_token },
    });
    // SDK грузится отдельным чанком только при первой попытке оплаты.
    const { initializePaddle } = await import('@paddle/paddle-js');
    const instance = await initializePaddle({
      environment: cfg.env as Environments,
      token: cfg.client_token,
      pwCustomer: pwCustomer(),
      eventCallback: (event) => {
        const raw = event as unknown as { name?: string; event_name?: string; type?: string };
        emitCheckoutEvent({ name: raw.name ?? raw.event_name ?? raw.type ?? '', data: event });
      },
    });
    if (!instance) throw new Error('Paddle.js returned no instance');
    paddle = instance;
    track('paddle_sdk_load_success', {
      metadata: { duration_ms: Date.now() - startedAt, env: cfg.env, has_instance: true },
    });
  } catch (error) {
    track('paddle_sdk_load_failed', {
      metadata: {
        duration_ms: Date.now() - startedAt,
        error_message: (error as Error)?.message,
        error_name: (error as Error)?.name,
      },
    });
    initPromise = null;
    throw error;
  }
}

/** Предел ожидания готовности Paddle.js: дольше человек не ждёт «Opening…». */
const READY_TIMEOUT_MS = 15_000;

/**
 * Ожидание с потолком: зависшая загрузка SDK (блокировщик, мёртвая сеть без
 * ошибки) превращается в отказ `subscribe_blocked_no_paddle`, а не в вечное
 * «opening». Сам initPromise не сбрасывается — доехавшая загрузка
 * переиспользуется следующей попыткой.
 */
function withReadyTimeout(promise: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Paddle.js is not ready in ${ms}ms`)), ms);
    promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (error) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

const liveGateway: CheckoutGateway = {
  ensureReady() {
    if (paddle && config) return Promise.resolve();
    if (!initPromise) initPromise = initLive();
    return withReadyTimeout(initPromise, READY_TIMEOUT_MS);
  },
  open({ params }) {
    if (!paddle) throw new Error('Paddle is not initialised');
    paddle.Checkout.open({
      items: [{ priceId: params.price_id, quantity: 1 }],
      customer: params.customer_id ? { id: params.customer_id } : { email: params.customer.email },
      customData: params.custom_data,
      settings: { displayMode: 'overlay', theme: uiTheme(), locale: uiLocale() },
    });
  },
};

// ——— Mock: оверлей приложения вместо Paddle ———
export interface MockCheckoutState {
  params: CheckoutParams;
  plan: PlanSlug;
}

let mockCheckout: MockCheckoutState | null = null;
const mockListeners = new Set<() => void>();

export function getMockCheckout(): MockCheckoutState | null {
  return mockCheckout;
}

export function subscribeMockCheckout(listener: () => void): () => void {
  mockListeners.add(listener);
  return () => mockListeners.delete(listener);
}

export function setMockCheckout(next: MockCheckoutState | null): void {
  mockCheckout = next;
  mockListeners.forEach((l) => l());
}

const mockGateway: CheckoutGateway = {
  ensureReady: () => Promise.resolve(),
  open(options) {
    setMockCheckout(options);
    setTimeout(() => emitCheckoutEvent({ name: 'checkout.loaded' }), 50);
  },
};

export const checkoutGateway: CheckoutGateway =
  appConfig.apiMode === 'mock' ? mockGateway : liveGateway;
